import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo, PrimaryButton } from "@/components/brand";
import { useLang, t, withLang } from "@/lib/i18n";

const NAV_LINKS = [
  { key: "merchants", to: "/for-merchants", label: { en: "For merchants", fr: "Pour marchands" } },
  { key: "drivers", to: "/for-drivers", label: { en: "For drivers", fr: "Pour livreurs" } },
  { key: "how", to: "/how-it-works", label: { en: "How it works", fr: "Comment ça marche" } },
  { key: "pricing", to: "/pricing", label: { en: "Pricing", fr: "Tarifs" } },
  { key: "faq", to: "/faq", label: { en: "FAQ", fr: "FAQ" } },
];

const LOGIN_LINKS = [
  { key: "m-login", to: "/login?role=merchant", label: { en: "Merchant login", fr: "Espace marchand" } },
  { key: "d-login", to: "/login?role=driver", label: { en: "Driver login", fr: "Espace livreur" } },
];

const testId = (label) => `nav-${label.toLowerCase().replace(/\s+/g, "-")}`;

function LangToggle({ lang }) {
  const location = useLocation();
  const buildHref = (target) => {
    const q = new URLSearchParams(location.search);
    if (target === "fr") q.set("lang", "fr"); else q.delete("lang");
    const qs = q.toString();
    return `${location.pathname}${qs ? `?${qs}` : ""}`;
  };
  return (
    <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase" data-testid="lang-toggle">
      <Link to={buildHref("en")} data-testid="lang-en"
        className={lang === "en" ? "text-[#0a0a0a]" : "text-gray-400 hover:text-[#0a0a0a]"}>EN</Link>
      <span className="text-gray-300">/</span>
      <Link to={buildHref("fr")} data-testid="lang-fr"
        className={lang === "fr" ? "text-[#0a0a0a]" : "text-gray-400 hover:text-[#0a0a0a]"}>FR</Link>
    </div>
  );
}

export default function MarketingNav() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const lang = useLang();

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-200">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to={withLang("/", lang)}><Logo /></Link>

        {/* Desktop */}
        <nav className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <Link key={l.key} to={withLang(l.to, lang)} data-testid={testId(t("en", l.label))}
              className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-500 hover:text-[#0a0a0a] transition-colors">
              {t(lang, l.label)}
            </Link>
          ))}
          <span className="h-4 w-px bg-gray-200" />
          <Link to={withLang("/login?role=merchant", lang)} data-testid="nav-merchant-login"
            className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-500 hover:text-[#0a0a0a] transition-colors">
            {t(lang, { en: "Merchant login", fr: "Espace marchand" })}
          </Link>
          <Link to={withLang("/login?role=driver", lang)} data-testid="nav-driver-login"
            className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-500 hover:text-[#0a0a0a] transition-colors">
            {t(lang, { en: "Driver login", fr: "Espace livreur" })}
          </Link>
          <LangToggle lang={lang} />
          <PrimaryButton data-testid="nav-apply-button" onClick={() => navigate(withLang("/apply", lang))} className="px-4 py-2 text-[13px]">
            {t(lang, { en: "Apply to drive", fr: "Postuler" })}
          </PrimaryButton>
        </nav>

        {/* Mobile toggle */}
        <div className="md:hidden flex items-center gap-3">
          <LangToggle lang={lang} />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
            className="grid h-10 w-10 place-items-center border border-gray-300 text-[#0a0a0a]"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-gray-200 bg-white" data-testid="nav-mobile-menu">
          <nav className="px-6 py-4 flex flex-col divide-y divide-gray-100">
            {NAV_LINKS.map((l) => (
              <Link key={l.key} to={withLang(l.to, lang)} onClick={() => setOpen(false)} data-testid={`m-${testId(t("en", l.label))}`}
                className="py-3.5 font-mono text-[12px] tracking-[0.14em] uppercase text-gray-600 hover:text-[#0a0a0a]">
                {t(lang, l.label)}
              </Link>
            ))}
            <div className="pt-2">
              <div className="py-2 font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400">
                {t(lang, { en: "Sign in", fr: "Se connecter" })}
              </div>
              {LOGIN_LINKS.map((l) => (
                <Link key={l.key} to={withLang(l.to, lang)} onClick={() => setOpen(false)} data-testid={`m-${testId(t("en", l.label))}`}
                  className="block py-3 font-mono text-[12px] tracking-[0.14em] uppercase text-gray-600 hover:text-[#0a0a0a]">
                  {t(lang, l.label)}
                </Link>
              ))}
            </div>
            <div className="pt-4">
              <PrimaryButton data-testid="m-nav-apply-button" onClick={() => { setOpen(false); navigate(withLang("/apply", lang)); }} className="w-full">
                {t(lang, { en: "Apply to drive", fr: "Postuler" })}
              </PrimaryButton>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
